import React, { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiDelete } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./PrescriptionPanel.css";

interface DrugGroup {
  group_id: number;
  group_name: string;
}

interface Drug {
  id: number;
  name: string;
  strength?: string;
  form?: string;
}

interface PendingDrug {
  drug_id: number;
  drug_name: string;
  strength: string;
  frequency: string;
  days: string;
  instructions: string;
}

interface SavedPrescription {
  id: number;
  medicines?: { drug_name?: string; name?: string; strength?: string; frequency?: string; dosage?: string; days?: string; instructions?: string }[];
}

interface PrescriptionPanelProps {
  patientId: number | null;
  visitId: number | null;
  isLocked?: boolean;
}

export default function PrescriptionPanel({ patientId, visitId, isLocked }: PrescriptionPanelProps) {
  const [savedPrescriptions, setSavedPrescriptions] = useState<SavedPrescription[]>([]);
  const [drugGroups, setDrugGroups] = useState<DrugGroup[]>([]);

  const [drugQuery, setDrugQuery] = useState("");
  const [drugResults, setDrugResults] = useState<Drug[]>([]);
  const [showResults, setShowResults] = useState(false);

  const [pendingDrugs, setPendingDrugs] = useState<PendingDrug[]>([]);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  const loadData = useCallback(async () => {
    if (!visitId) return;
    try {
      const [saved, groups] = await Promise.all([
        apiGet(`/api/doctor/prescriptions/visit/${visitId}`) as Promise<SavedPrescription[]>,
        apiGet("/api/admin/drug-groups").catch(() => []) as Promise<Array<{ id?: number; name?: string; group_id?: number; group_name?: string }>>
      ]);
      setSavedPrescriptions(Array.isArray(saved) ? saved : []);
      const mappedGroups = (Array.isArray(groups) ? groups : []).map((g) => ({
        group_id: (g as { id?: number; group_id?: number }).id ?? (g as { group_id?: number }).group_id ?? 0,
        group_name: (g as { name?: string; group_name?: string }).name ?? (g as { group_name?: string }).group_name ?? ""
      })).filter((g) => g.group_id && g.group_name);
      setDrugGroups(mappedGroups);
    } catch (err) {
      console.error("Error loading Rx data", err);
      showToast({ message: "Failed to load prescription data.", type: "error" });
    }
  }, [visitId, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (drugQuery.length < 2) {
      setDrugResults([]);
      setShowResults(false);
      return;
    }
    const timer = setTimeout(() => {
      apiGet(`/api/doctor/drugs?q=${encodeURIComponent(drugQuery)}`)
        .then((data: unknown) => {
          setDrugResults(Array.isArray(data) ? (data as Drug[]) : []);
          setShowResults(true);
        })
        .catch(() => {
          setDrugResults([]);
          setShowResults(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [drugQuery]);

  const isDuplicateDraft = (drugId: number) =>
    pendingDrugs.some((d) => d.drug_id === drugId);

  const defaultFreq = "1-0-1";
  const defaultDays = "3";
  const defaultInstr = "After Food";

  const addDrug = (drug: Drug) => {
    if (isDuplicateDraft(drug.id)) return;
    setPendingDrugs((prev) => [
      ...prev,
      {
        drug_id: drug.id,
        drug_name: drug.name,
        strength: drug.strength || "",
        frequency: defaultFreq,
        days: defaultDays,
        instructions: defaultInstr
      }
    ]);
    setDrugQuery("");
    setShowResults(false);
  };

  const loadKit = async (groupId: string) => {
    if (!groupId) return;
    try {
      const items = await apiGet(`/api/admin/drug-groups/${groupId}/items`) as { id: number; name: string; strength?: string; default_times_per_day?: string; default_days?: string }[];
      if (!Array.isArray(items)) return;
      const newDrugs = items
        .filter((i) => !isDuplicateDraft(i.id))
        .map((i) => ({
          drug_id: i.id,
          drug_name: i.name,
          strength: i.strength || "",
          frequency: i.default_times_per_day || defaultFreq,
          days: i.default_days || defaultDays,
          instructions: defaultInstr
        }));
      if (newDrugs.length === 0) return;
      setPendingDrugs((prev) => [...prev, ...newDrugs]);
    } catch (e) {
      console.error("Failed to load kit", e);
      showToast({ message: "Failed to load kit.", type: "error" });
    }
  };

  const updatePending = (index: number, field: keyof PendingDrug, value: string) => {
    setPendingDrugs((prev) => {
      const u = [...prev];
      u[index] = { ...u[index], [field]: value };
      return u;
    });
  };

  const removePending = (index: number) => {
    setPendingDrugs((prev) => prev.filter((_, i) => i !== index));
  };

  const deleteSaved = async (id: number) => {
    if (!window.confirm("Remove this prescription?")) return;
    try {
      await apiDelete(`/api/doctor/prescriptions/${id}`);
      showToast({ message: "Prescription removed", type: "success" });
      loadData();
    } catch (e) {
      console.error("Delete failed", e);
      showToast({ message: "Failed to delete prescription.", type: "error" });
    }
  };

  const saveAll = async () => {
    if (pendingDrugs.length === 0) return;
    setSaving(true);
    try {
      const medicines = pendingDrugs.map((d) => ({
        drug_id: d.drug_id,
        drug_name: d.drug_name,
        strength: d.strength,
        frequency: d.frequency,
        days: d.days,
        instructions: d.instructions
      }));
      await apiPost("/api/doctor/prescriptions", {
        visit_id: visitId,
        patient_id: patientId,
        medicines,
        instructions: ""
      });
      setPendingDrugs([]);
      showToast({ message: "Prescriptions saved successfully", type: "success" });
      loadData();
    } catch (e) {
      console.error("Save prescriptions failed", e);
      showToast({ message: "Failed to save prescriptions.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rx-panel">
      <div className="rx-header mod-header-wrap">
        <div className="rx-title">
          <h3>💊 Prescription Writer</h3>
          <span>Manage patient medications</span>
        </div>
        <div className="rx-controls">
          <select
            className="kit-select"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => loadKit(e.target.value)}
            defaultValue=""
            disabled={isLocked}
          >
            <option value="" disabled>Load Preset Kit...</option>
            {drugGroups.map((g) => (
              <option key={g.group_id} value={g.group_id}>
                📦 {g.group_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rx-search-wrapper">
        <span className="search-icon">🔍</span>
        <input
          className="rx-search-input"
          placeholder="Search drug (min 2 chars)..."
          value={drugQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDrugQuery(e.target.value)}
          onFocus={() => drugQuery.length >= 2 && setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 150)}
          disabled={isLocked}
        />
        {showResults && drugResults.length > 0 && (
          <ul className="rx-search-results">
            {drugResults.map((d) => (
              <li
                key={d.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addDrug(d);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") addDrug(d);
                }}
                role="button"
                tabIndex={0}
              >
                <div className="res-main">
                  <strong>{d.name}</strong>
                  <span className="res-strength">{d.strength}</span>
                </div>
                <span className="res-type">{d.form}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rx-split-view">
        <div className="rx-section draft-section">
          <div className="sec-head">
            <h4>📝 Pending Draft ({pendingDrugs.length})</h4>
            {pendingDrugs.length > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={saveAll}
                disabled={saving || isLocked}
              >
                {saving ? "Saving..." : "💾 Save All"}
              </Button>
            )}
          </div>
          {pendingDrugs.length === 0 ? (
            <div className="empty-draft">
              <span>Use search or kits to add medicines.</span>
            </div>
          ) : (
            <div className="draft-list">
              {pendingDrugs.map((item, idx) => (
                <div key={idx} className="draft-item">
                  <div className="draft-info">
                    <strong>{item.drug_name}</strong>
                    <small>{item.strength}</small>
                  </div>
                  <div className="draft-inputs">
                    <input
                      placeholder="Freq"
                      className="inp-sm"
                      value={item.frequency}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePending(idx, "frequency", e.target.value)}
                      disabled={isLocked}
                    />
                    <input
                      placeholder="Days"
                      className="inp-sm"
                      value={item.days}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePending(idx, "days", e.target.value)}
                      disabled={isLocked}
                    />
                    <input
                      placeholder="Instructions..."
                      className="inp-lg"
                      value={item.instructions}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePending(idx, "instructions", e.target.value)}
                      disabled={isLocked}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removePending(idx)}
                    title="Remove"
                    disabled={isLocked}
                    style={{ color: '#ef4444' }}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rx-section saved-section">
          <div className="sec-head">
            <h4>✅ Prescribed for this Visit</h4>
          </div>
          {savedPrescriptions.length === 0 ? (
            <div className="empty-saved">
              <span className="icon">📂</span>
              <p>No prescriptions saved yet.</p>
            </div>
          ) : (
            <div className="saved-list">
              {savedPrescriptions.map((p) => {
                const meds = Array.isArray(p.medicines) ? p.medicines : [];
                return meds.map((m, i) => (
                  <div key={`${p.id}-${i}`} className="saved-card">
                    <div className="card-main">
                      <div className="drug-line">
                        <span className="d-name">{m.drug_name || m.name}</span>
                        <span className="d-strength">{m.strength}</span>
                      </div>
                      <div className="dose-line">
                        <span className="badge-freq">{m.frequency}</span>
                        <span className="text-days">for {m.days} days</span>
                      </div>
                      {m.instructions && (
                        <div className="instr-line">&quot;{m.instructions}&quot;</div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteSaved(p.id)}
                      title="Delete"
                      disabled={isLocked}
                    >
                      🗑️
                    </Button>
                  </div>
                ));
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
